import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  HelpCircle,
  Lightbulb,
  BookOpen,
  FileText,
  Sparkles,
  Target,
  MessageSquare,
  SpellCheck,
  Mic,
  Brain,
} from "lucide-react";
import { ChatMessage, type AIStatus } from "./ChatMessage";
import { cn } from "@/lib/utils";
import type { Message } from "@/contexts/AIAssistantContext";
import { Mascot } from "@/components/mascot";
interface QuestionContext {
  questionText: string;
  difficulty: string;
  keyPoints?: string[];
  sessionTopic: string;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  onEditMessage: (messageIndex: number) => void;
  sendMessage: (message: string) => void;
  questionContext?: QuestionContext;
}

export function ChatMessages({
  messages,
  isLoading,
  onEditMessage,
  sendMessage,
  questionContext,
}: ChatMessagesProps) {
  const { t } = useTranslation();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  // Track if user has scrolled away from bottom
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    autoScrollRef.current = isAtBottom;
  };

  // Auto-scroll to bottom only if user is at bottom
  useEffect(() => {
    if (!autoScrollRef.current) return;
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  const isEmpty = messages.length === 0;
  const lastAssistantMessage =
    isLoading && messages.length > 0 && messages[messages.length - 1]?.role === "assistant"
      ? messages[messages.length - 1]
      : null;

  const aiStatus: AIStatus | undefined = lastAssistantMessage
    ? lastAssistantMessage.content === ""
      ? "thinking"
      : "generating"
    : undefined;

  // Question-focused suggestions for practice mode
  const questionSuggestions = questionContext
    ? [
        {
          text: "Explain what this question is asking",
          icon: HelpCircle,
          color: "text-sky-500",
          bg: "bg-sky-500/10",
        },
        {
          text: "Give me hints on how to answer",
          icon: Lightbulb,
          color: "text-amber-500",
          bg: "bg-amber-500/10",
        },
        {
          text: "Help me with vocabulary for this topic",
          icon: BookOpen,
          color: "text-primary",
          bg: "bg-primary/10",
        },
        {
          text: "Show me a sample answer structure",
          icon: FileText,
          color: "text-success",
          bg: "bg-success/10",
        },
      ]
    : null;

  // Education-focused suggestions organized by learning category
  const genericSuggestions = [
    {
      text: "Explain a grammar rule",
      icon: SpellCheck,
      color: "text-primary",
      bg: "bg-primary/10",
      category: "Grammar",
    },
    {
      text: "Check my sentence",
      icon: FileText,
      color: "text-sky-500",
      bg: "bg-sky-500/10",
      category: "Grammar",
    },
    {
      text: "Teach me new vocabulary",
      icon: BookOpen,
      color: "text-success",
      bg: "bg-success/10",
      category: "Vocabulary",
    },
    {
      text: "Word usage examples",
      icon: Brain,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      category: "Vocabulary",
    },
    {
      text: "How to pronounce this?",
      icon: Mic,
      color: "text-pink-500",
      bg: "bg-pink-500/10",
      category: "Speaking",
    },
    {
      text: "Give me fluency tips",
      icon: Target,
      color: "text-orange-500",
      bg: "bg-orange-500/10",
      category: "Speaking",
    },
  ];

  if (isEmpty) {
    // Group generic suggestions by category
    const categories = questionContext
      ? null
      : [
          { label: "Grammar", items: genericSuggestions.filter((s) => s.category === "Grammar") },
          { label: "Vocabulary", items: genericSuggestions.filter((s) => s.category === "Vocabulary") },
          { label: "Speaking", items: genericSuggestions.filter((s) => s.category === "Speaking") },
        ];

    return (
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center text-center px-4 py-6">
        <Mascot mood="idle" size="sm" />
        <h4 className="font-bold text-base mb-1 mt-3">
          {questionContext
            ? t("pages.aiAssistant.practiceCoach", "Practice Coach")
            : t("pages.aiAssistant.learningCoach", "Learning Coach")}
        </h4>
        <p className="text-xs text-muted-foreground mb-5 max-w-[240px] leading-relaxed">
          {questionContext
            ? t("pages.aiAssistant.subtitlePractice", "Ask for hints, explanations, vocabulary help, or sample answers.")
            : t("pages.aiAssistant.subtitleGeneric", "Your personal language learning companion")}
        </p>

        {questionContext ? (
          <div className="grid gap-2 w-full max-w-[300px]">
            {questionSuggestions?.map((suggestion) => {
              const Icon = suggestion.icon;
              return (
                <button
                  key={suggestion.text}
                  onClick={() => sendMessage(suggestion.text)}
                  className="flex items-center gap-3 rounded-xl border border-border/50 bg-card hover:bg-accent hover:border-border p-3 text-left transition-colors group"
                >
                  <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0", suggestion.bg)}>
                    <Icon className={cn("h-4 w-4", suggestion.color)} />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {suggestion.text}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="w-full max-w-[320px] space-y-4">
            {categories?.map((category) => (
              <div key={category.label}>
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 text-left">
                  {category.label}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {category.items.map((suggestion) => {
                    const Icon = suggestion.icon;
                    return (
                      <button
                        key={suggestion.text}
                        onClick={() => sendMessage(suggestion.text)}
                        className="flex items-center gap-2.5 rounded-xl border border-border/50 bg-card hover:bg-accent hover:border-border p-2.5 text-left transition-colors group"
                      >
                        <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0", suggestion.bg)}>
                          <Icon className={cn("h-3.5 w-3.5", suggestion.color)} />
                        </div>
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors leading-tight">
                          {suggestion.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="flex-1 min-h-0 overflow-y-auto p-3 md:p-4 scrollbar-themed"
    >
      <div className="space-y-3">
        {messages.map((message, index) => {
          const messageIndex = messages.findIndex((m) => m.id === message.id);
          const isLastAssistant =
            index === messages.length - 1 && message.role === "assistant";
          return (
            <ChatMessage
              key={message.id}
              message={message}
              onEdit={
                message.role === "user"
                  ? () => onEditMessage(messageIndex)
                  : undefined
              }
              aiStatus={isLastAssistant ? aiStatus : undefined}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
